import { useNavigate } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { useRef, useCallback } from 'react';
import { SimilarSearchProgressAPI } from '../../common/SimilarSearchProgress';
import { fetchToken } from '../../../../lib/globalApiClient';

// Backend API URL for image proxy
const API_URL = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';
const SIMILAR_SEARCH_API = process.env.REACT_APP_APIUrl1688 + "/get-product-info-by-image";
const IMAGE_SEARCH_PAGE_SIZE = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '12', 10);

// Cache for similar search results (avoids re-searching same images)
const similarSearchCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

// Optimized compression settings
const COMPRESSION_CONFIG = {
  maxDimension: 400,      // Reduced from 600 - smaller = faster upload & processing
  targetSizeKB: 80,       // Reduced from 150KB - faster upload
  initialQuality: 0.7,    // Start lower to reduce compression iterations
  minQuality: 0.5,        // Minimum acceptable quality
  qualityStep: 0.1,       // Quality reduction per iteration
};

export const usePdpFindSimilar = ({ product, preferredImage }) => {
  const navigate = useNavigate();
  const message = useMessage();
  const abortControllerRef = useRef(null);

  // Fast image compression with single-pass optimization
  const compressImageFast = useCallback(async (blob) => {
    const { maxDimension, targetSizeKB, initialQuality, minQuality, qualityStep } = COMPRESSION_CONFIG;

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);

        // Calculate dimensions maintaining aspect ratio
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        // Use OffscreenCanvas if available for better performance
        const canvas = typeof OffscreenCanvas !== 'undefined'
          ? new OffscreenCanvas(width, height)
          : document.createElement('canvas');

        if (!(canvas instanceof OffscreenCanvas)) {
          canvas.width = width;
          canvas.height = height;
        }

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Single-pass compression with adaptive quality
        const targetBytes = targetSizeKB * 1024;
        let quality = initialQuality;

        const tryCompress = () => {
          const convertToBlob = canvas instanceof OffscreenCanvas
            ? () => canvas.convertToBlob({ type: 'image/jpeg', quality })
            : () => new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));

          convertToBlob().then(compressedBlob => {
            if (compressedBlob && (compressedBlob.size <= targetBytes || quality <= minQuality)) {
              resolve(compressedBlob);
            } else {
              quality -= qualityStep;
              if (quality >= minQuality) {
                tryCompress();
              } else {
                resolve(compressedBlob || blob);
              }
            }
          }).catch(() => resolve(blob));
        };

        tryCompress();
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };

      img.src = url;
    });
  }, []);

  // Optimized API call with shorter timeout and smart retry
  const callSimilarSearchAPI = useCallback(async (base64Image, signal) => {
    const payload = {
      imageBase64: base64Image,
      beginPage: 1,
      pageSize: IMAGE_SEARCH_PAGE_SIZE,
      country: 'en',
    };

    const token = await fetchToken();
    const response = await fetch(SIMILAR_SEARCH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  }, []);

  // Optimized URL normalization
  const normalizeImageUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return null;

    let normalized = url.trim();

    // Protocol fixes
    if (normalized.startsWith('//')) {
      normalized = 'https:' + normalized;
    } else if (normalized.startsWith('http://')) {
      normalized = normalized.replace('http://', 'https://');
    } else if (!normalized.startsWith('https://') && normalized.includes('.') && !normalized.includes(' ')) {
      normalized = 'https://' + normalized;
    }

    // Remove size modifiers for better quality source image
    normalized = normalized.replace(/\.(\d+x\d+)\.(jpg|jpeg|png|webp)/gi, '.$2');

    // Validate final URL
    if (!normalized.startsWith('http')) return null;

    return normalized;
  }, []);

  // Get best image URL from product with priority order
  const getBestImageUrl = useCallback(() => {
    const candidates = [
      preferredImage,
      product?.imgUrl?.[0],
      product?.imageUrl,
      product?.image,
      product?.mainImage,
    ];

    for (const url of candidates) {
      if (typeof url === 'string' && url.trim()) {
        const normalized = normalizeImageUrl(url);
        if (normalized) return normalized;
      }
    }
    return null;
  }, [preferredImage, product, normalizeImageUrl]);

  // Extract keywords from title for fallback search
  const extractSearchKeywords = useCallback((title) => {
    if (!title) return '';

    const stopWords = new Set([
      'the', 'and', 'for', 'with', 'from', 'that', 'this', 'you', 'your', 'our',
      'are', 'was', 'were', 'will', 'can', 'able', 'into', 'out', 'not', 'non',
      'item', 'items', 'of', 'in', 'on', 'to', 'by', 'is', 'it', 'at', 'as',
      'or', 'an', 'a', 'be', 'has', 'have', 'new', 'sale', 'best', 'high', 'quality',
      'free', 'shipping', 'hot', 'pcs', 'set', 'lot', 'pack', 'wholesale'
    ]);

    const words = title
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w));

    // Prioritize longer/more descriptive words
    words.sort((a, b) => b.length - a.length);

    return words.slice(0, 4).join(' ');
  }, []);

  const handleFindSimilar = useCallback(async (e) => {
    if (e?.stopPropagation) e.stopPropagation();

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    const imageUrl = getBestImageUrl();
    if (!imageUrl) {
      message.showToast('No image available for similar search', ToastTypes.WARNING);
      return;
    }

    // Check cache first
    const cacheKey = imageUrl.substring(0, 200);
    const cached = similarSearchCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log('⚡ Using cached similar search results');
      const timestamp = Date.now().toString();
      const cachedList = (Array.isArray(cached.products) ? cached.products : []).slice(0, IMAGE_SEARCH_PAGE_SIZE);
      sessionStorage.setItem('alibaba_image_results', JSON.stringify(cachedList));
      sessionStorage.setItem('alibaba_image_results_timestamp', timestamp);
      if (cached.pagination) {
        sessionStorage.setItem('alibaba_image_search_pagination', JSON.stringify({ ...cached.pagination, pageSize: IMAGE_SEARCH_PAGE_SIZE }));
      }
      navigate(`/collection?ali=ready&category=global&t=${timestamp}`);
      return;
    }

    // Show progress loader
    SimilarSearchProgressAPI.show();
    SimilarSearchProgressAPI.setStage('STARTING');

    try {
      // Stage 1: Fetching image
      SimilarSearchProgressAPI.setStage('FETCHING_IMAGE');

      const proxyUrl = `${API_URL.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResponse = await fetch(proxyUrl, { signal });

      if (!imgResponse.ok) {
        throw new Error(`Failed to fetch image: ${imgResponse.status}`);
      }

      SimilarSearchProgressAPI.setStage('IMAGE_DOWNLOADED');
      let imgBlob = await imgResponse.blob();

      // Stage 2: Compressing
      SimilarSearchProgressAPI.setStage('COMPRESSING');
      const originalSize = imgBlob.size;
      imgBlob = await compressImageFast(imgBlob);
      console.log(`📦 Image: ${originalSize} → ${imgBlob.size} bytes (${Math.round((1 - imgBlob.size / originalSize) * 100)}% reduction)`);

      SimilarSearchProgressAPI.setStage('COMPRESSED');

      // Convert to base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      // Stage 3: API call
      SimilarSearchProgressAPI.setStage('SENDING_API');

      // Set timeout for API call
      const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), 35000);

      // Progress to API_PROCESSING after a short delay
      setTimeout(() => SimilarSearchProgressAPI.setStage('API_PROCESSING'), 500);

      const data = await callSimilarSearchAPI(base64Image, signal);
      clearTimeout(timeoutId);

      const productsRaw = data?.products || data?.result || data?.data || [];
      const products = (Array.isArray(productsRaw) ? productsRaw : []).slice(0, IMAGE_SEARCH_PAGE_SIZE);

      if (products.length > 0) {
        // Cache the results
        similarSearchCache.set(cacheKey, {
          products,
          pagination: data.pagination ? { ...data.pagination, pageSize: IMAGE_SEARCH_PAGE_SIZE } : data.pagination,
          timestamp: Date.now(),
        });

        if (similarSearchCache.size > 20) {
          const oldestKey = similarSearchCache.keys().next().value;
          similarSearchCache.delete(oldestKey);
        }

        // Complete the progress
        SimilarSearchProgressAPI.complete();

        if (data.pagination) {
          sessionStorage.setItem('alibaba_image_search_pagination', JSON.stringify({ ...data.pagination, pageSize: IMAGE_SEARCH_PAGE_SIZE }));
        }
        if (data.imageId) {
          sessionStorage.setItem('alibaba_image_search_imageId', data.imageId);
        }

        sessionStorage.setItem('alibaba_image_search_base64', `data:image/jpeg;base64,${base64Image}`);

        const timestamp = Date.now().toString();
        sessionStorage.setItem('alibaba_image_results', JSON.stringify(products));
        sessionStorage.setItem('alibaba_image_results_timestamp', timestamp);

        // Navigate after progress animation completes
        setTimeout(() => {
          navigate(`/collection?ali=ready&category=global&t=${timestamp}`);
        }, 600);
      } else {
        // No results - fallback to keyword search
        SimilarSearchProgressAPI.hide();

        const searchTerms = extractSearchKeywords(product?.title);
        if (searchTerms) {
          message.showToast('Searching by keywords...', ToastTypes.INFO);
          navigate(`/collection?ali=keyword&search=${encodeURIComponent(searchTerms)}&category=global`);
        } else {
          message.showToast('No similar products found', ToastTypes.INFO);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Search cancelled');
        SimilarSearchProgressAPI.hide();
        return;
      }

      console.error('❌ Find similar error:', error.message);
      SimilarSearchProgressAPI.error();

      // Smart fallback to keyword search
      const searchTerms = extractSearchKeywords(product?.title);
      if (searchTerms) {
        console.log('🔄 Fallback to keyword search:', searchTerms);
        setTimeout(() => {
          message.showToast('Using keyword search...', ToastTypes.INFO);
          navigate(`/collection?ali=keyword&search=${encodeURIComponent(searchTerms)}&category=global`);
        }, 1200);
      } else {
        const userMessage = error.message.includes('timeout')
          ? 'Request timed out. Please try again.'
          : 'Unable to find similar products';
        setTimeout(() => {
          message.showToast(userMessage, ToastTypes.ERROR);
        }, 1200);
      }
    }
  }, [getBestImageUrl, compressImageFast, callSimilarSearchAPI, extractSearchKeywords, message, navigate, product]);

  return handleFindSimilar;
};
